import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Lock } from 'lucide-react';
import {
  passwordResetConfirmSchema,
  type PasswordResetConfirmInput,
} from '@/shared/validation/auth';
import { api, ApiError } from '@/client/lib/api';
import { Button } from '@/client/components/ui/button';
import { Input } from '@/client/components/ui/input';
import { Label } from '@/client/components/ui/label';
import { Alert } from '@/client/components/ui/alert';
import { isLocale, type Locale } from '@/client/i18n';
import type { Me } from '@/client/hooks/useMe';

export function ResetPassword() {
  const { t } = useTranslation();
  const params = useParams();
  const locale: Locale = isLocale(params.locale) ? params.locale : 'sk';
  const [search] = useSearchParams();
  const tokenFromUrl = search.get('token') ?? '';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [serverError, setServerError] = useState<string | null>(null);

  const form = useForm<PasswordResetConfirmInput>({
    resolver: zodResolver(passwordResetConfirmSchema),
    defaultValues: { token: tokenFromUrl, newPassword: '' },
  });

  async function onSubmit(values: PasswordResetConfirmInput) {
    setServerError(null);
    try {
      const data = await api<{ user: Me }>('/api/v1/auth/password/reset/confirm', {
        method: 'POST',
        body: values,
      });
      queryClient.setQueryData(['me'], data.user);
      navigate(`/${locale}/portal`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setServerError(t('auth.errors_invalid_token'));
      } else {
        setServerError(t('errors.generic'));
      }
    }
  }

  return (
    <div className="relative">
      <div className="mx-auto max-w-md px-5 sm:px-6 py-12 sm:py-20">
        <div className="dr-card-strong p-8 sm:p-10 animate-fade-up">
          <div className="text-center">
            <div className="mx-auto h-14 w-14 mb-5 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/15 flex items-center justify-center">
              <Lock size={24} className="text-primary" />
            </div>
            <p className="dr-eyebrow">
              {t('auth.reset_eyebrow', { defaultValue: 'Nové heslo' })}
            </p>
            <h1 className="mt-3 font-display text-h2 text-primary-dark">
              {t('auth.reset_title')}
            </h1>
            <p className="mt-2 font-sans text-body text-text-secondary">
              {t('auth.reset_subtitle', {
                defaultValue: 'Zadaj si nové heslo a budeš automaticky prihlásená.',
              })}
            </p>
          </div>

          {serverError && (
            <Alert variant="error" className="mt-6">
              {serverError}
            </Alert>
          )}

          <form onSubmit={form.handleSubmit(onSubmit)} className="mt-8 space-y-4" noValidate>
            <input type="hidden" {...form.register('token')} />
            <div>
              <Label htmlFor="newPassword">{t('auth.new_password_label')}</Label>
              <Input
                id="newPassword"
                type="password"
                autoComplete="new-password"
                {...form.register('newPassword')}
              />
              {form.formState.errors.newPassword && (
                <p className="mt-1.5 text-caption text-error">
                  {form.formState.errors.newPassword.message}
                </p>
              )}
            </div>
            <Button
              type="submit"
              className="w-full mt-2"
              size="lg"
              disabled={form.formState.isSubmitting}
            >
              {form.formState.isSubmitting ? t('common.loading') : t('auth.reset_submit')}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Link
              to={`/${locale}/prihlasenie`}
              className="inline-flex items-center gap-2 font-sans text-body-small text-primary hover:gap-3 transition-all"
            >
              <ArrowLeft size={14} />
              {t('nav.login')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
